import { motion } from 'framer-motion'
import { Award, Users, Target, Heart, Shield, Zap } from 'lucide-react'

const About = () => {
  const values = [
    {
      icon: Shield,
      title: 'Qualidade',
      description: 'Compromisso com a excelência em todos os nossos serviços e produtos'
    },
    {
      icon: Users,
      title: 'Confiança',
      description: 'Relacionamentos duradouros baseados na transparência e honestidade'
    },
    {
      icon: Zap,
      title: 'Inovação',
      description: 'Sempre em busca das mais modernas tecnologias e soluções'
    },
    {
      icon: Heart,
      title: 'Dedicação',
      description: 'Paixão pelo que fazemos e foco na satisfação do cliente'
    }
  ]

  const team = [
    {
      name: 'Claudiomir Luciano',
      position: 'Fundador Técnico em Refrigeração',
      description: 'Técnico em refrigeração com 20 anos de experiência no setor'
    },
    {
      name: 'Gabriel Cardoso Kuhnen',
      position: 'Fundador técnico em Refrigeração',
      description: 'Especialista em refrigeração industrial e projetos personalizados'
    },
    {
      name: 'Luciane Luciano',
      position: 'Administrativo Financeiro',
      description: 'Responsável pelo controle financeiro e administrativo'
    }
  ]

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section 
        className="py-20 bg-gradient-to-r from-blue-900 to-cyan-800 text-white"
        aria-labelledby="about-hero-title"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 id="about-hero-title" className="text-5xl md:text-6xl font-bold mb-6">
              Sobre a Tech Glacial
            </h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto">
              15 anos transformando ambientes através de soluções inovadoras 
              em refrigeração e climatização
            </p>
          </motion.div>
        </div>
      </section>

      {/* Company Story */}
      <section 
        className="py-20 bg-white"
        aria-labelledby="company-story-title"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 id="company-story-title" className="text-4xl font-bold text-gray-900 mb-6">
                Nossa História
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                Fundada em 2025 por Claudiomir Luciano e Gabriel Cardoso Kuhnen, a Tech Glacial nasceu 
                com o objetivo de revolucionar o mercado de refrigeração e climatização 
                no Brasil. Começamos como uma pequena empresa familiar e hoje somos 
                referência nacional no setor.
              </p>
              <p className="text-lg text-gray-700 mb-6">
                Nossa jornada é marcada pela busca constante da excelência, 
                investimento em tecnologia de ponta e formação de uma equipe 
                altamente qualificada. Atendemos desde residências até grandes 
                complexos industriais, sempre com o mesmo compromisso: qualidade 
                e satisfação do cliente.
              </p>
              <p className="text-lg text-gray-700">
                Com uma vasta experiencia de mais de 20 anos, continuamos crescendo e inovando para oferecer 
                as melhores soluções do mercado.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <img
                src="https://images.pexels.com/photos/159213/hall-congress-architecture-building-159213.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Edifício moderno representando a sede da Tech Glacial, com arquitetura contemporânea em vidro e concreto"
                className="rounded-lg shadow-xl"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-cyan-500/20 rounded-lg" aria-hidden="true"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section 
        className="py-20 bg-gray-50"
        aria-labelledby="mission-vision-title"
      >
        <h2 id="mission-vision-title" className="sr-only">Missão e Visão</h2>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <motion.article
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="bg-white p-8 rounded-xl shadow-lg"
              aria-labelledby="mission-title"
            >
              <div 
                className="bg-gradient-to-r from-blue-600 to-cyan-500 w-16 h-16 rounded-full flex items-center justify-center mb-6"
                aria-hidden="true"
              >
                <Target className="h-8 w-8 text-white" />
              </div>
              <h3 id="mission-title" className="text-2xl font-bold text-gray-900 mb-4">
                Nossa Missão
              </h3>
              <p className="text-gray-700">
                Proporcionar soluções completas e inovadoras em refrigeração e 
                climatização, superando expectativas e contribuindo para o 
                conforto e qualidade de vida de nossos clientes, com 
                responsabilidade ambiental e social.
              </p>
            </motion.article>

            <motion.article
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="bg-white p-8 rounded-xl shadow-lg"
              aria-labelledby="vision-title"
            >
              <div 
                className="bg-gradient-to-r from-blue-600 to-cyan-500 w-16 h-16 rounded-full flex items-center justify-center mb-6"
                aria-hidden="true"
              >
                <Award className="h-8 w-8 text-white" />
              </div>
              <h3 id="vision-title" className="text-2xl font-bold text-gray-900 mb-4">
                Nossa Visão
              </h3>
              <p className="text-gray-700">
                Ser reconhecida como a empresa líder em soluções de refrigeração 
                e climatização no Brasil, referência em qualidade, inovação e 
                sustentabilidade, expandindo nossa presença para toda a América Latina.
              </p>
            </motion.article>
          </div>
        </div>
      </section>

      {/* Values */}
      <section 
        className="py-20 bg-white"
        aria-labelledby="values-title"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 id="values-title" className="text-4xl font-bold text-gray-900 mb-4">
              Nossos Valores
            </h2>
            <p className="text-xl text-gray-600">
              Os princípios que guiam todas as nossas ações
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.article
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
                aria-labelledby={`value-${index}-title`}
              >
                <div 
                  className="bg-gradient-to-r from-blue-600 to-cyan-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                  aria-hidden="true"
                >
                  <value.icon className="h-8 w-8 text-white" />
                </div>
                <h3 id={`value-${index}-title`} className="text-xl font-semibold text-gray-900 mb-2">
                  {value.title}
                </h3>
                <p className="text-gray-600">
                  {value.description}
                </p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section 
        className="py-20 bg-gray-50"
        aria-labelledby="team-title"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 id="team-title" className="text-4xl font-bold text-gray-900 mb-4">
              Nossa Equipe
            </h2>
            <p className="text-xl text-gray-600">
              Profissionais experientes e dedicados ao seu sucesso
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <motion.article
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl shadow-lg overflow-hidden"
                aria-labelledby={`team-member-${index}-name`}
              >
                <div className="p-6">
                  <h3 
                    id={`team-member-${index}-name`}
                    className="text-xl font-semibold text-gray-900 mb-1"
                  >
                    {member.name}
                  </h3>
                  <p className="text-blue-600 font-medium mb-3">
                    {member.position}
                  </p>
                  <p className="text-gray-600 text-sm">
                    {member.description}
                  </p>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}

export default About